import { S } from './state.js';
import { startWave } from './combat.js';
import { ITEMS } from './data/items.js';
import { GAME_VERSION } from './data/progression.js';
import { WEAPONS, WEAPON_IDX, validateData } from './data/weapons.js';
import { player } from './entities.js';
import { grantItem, randomMythic } from './economy.js';
import { popText } from './procs.js';
import { pushOutOfRocks } from './world.js';
// ---- Damage meter: per-source effective damage dealt this run (overkill clamped out) ----
S.dmgBySrc = {};   // { srcKey: totalDamage } — reset each run, tallied in damageEnemy + DoT ticks
// #dev cheat prompt (backtick). Commands keep to existing systems — grantItem, startWave, the real
// shrine spawner — so cheated state is indistinguishable from earned state (what you test is what ships).
export function devCommand() {
  const inp = (prompt('dev> item <key> [n] · mythic · wave <n> · coins <n> · god · heal · vault · lint') || '').trim().split(/\s+/);
  const cmd = inp[0];
  if (cmd === 'item' && inp[1]) {
    if (!ITEMS.some(i => i.key === inp[1])) { console.warn('dev: no item ' + inp[1]); return; }
    for (let i = 0; i < (parseInt(inp[2]) || 1); i++) grantItem(inp[1]);
  }
  else if (cmd === 'mythic') { const m = randomMythic(); if (m) grantItem(m.key); }
  else if (cmd === 'wave' && S.started) { S.enemies = []; S.warnings = []; S.bullets = []; S.mortars = []; S.waveNum = Math.max(0, (parseInt(inp[1]) || 1) - 1); startWave(++S.waveNum); S.waveTimer = S.WAVE_INTERVAL; }
  else if (cmd === 'coins') { const n = parseInt(inp[1]) || 1000; S.coinsRun += n; S.coinsEarned += n; }
  else if (cmd === 'god') { S.devGod = !S.devGod; popText({ x: player.x, y: player.y - 30 }, S.devGod ? 'GOD ON' : 'GOD OFF'); }
  else if (cmd === 'heal') player.hp = S.MAX_HP;
  else if (cmd === 'vault') { const vp = { x: player.x + 120, y: player.y }; pushOutOfRocks(vp, 70); S.shrines.push({ x: vp.x, y: vp.y, kind: 'vault', blood: false, item: null, choices: null, taken: false, ph: 0 }); }
  else if (cmd === 'lint') validateData();
}
// display order, labels & bar colors; any source not listed still tallies and renders last under its raw key
// Damage-meter sources (0.14: fully split for balancing — every item/weapon that deals damage has
// its OWN row, and the same keys ship in telemetry). 'w:<weapon>' keys are per-weapon bullet damage.
export const DMG_SRC_META = [
  { key: 'bullet',     name: 'Bullets',      col: '#7fd3ff' },   // legacy (pre-split logs)
  { key: 'burn',       name: 'Burn',         col: '#ff8a3d' },
  { key: 'poison',     name: 'Poison',       col: '#8be04e' },
  { key: 'tesla',      name: 'Tesla Coil',   col: '#c9a3ff' },
  { key: 'stormcore',  name: 'Storm Core',   col: '#b39dff' },
  { key: 'arcwell',    name: 'Arc Well',     col: '#9fb8ff' },
  { key: 'teslalance', name: 'Tesla Lance',  col: '#8fd0ff' },
  { key: 'arc',        name: 'Arc Coil',     col: '#9fe6ff' },
  { key: 'volatile',   name: 'Volatile',     col: '#ffb74d' },
  { key: 'blastcap',   name: 'Blast Cap',    col: '#ffd24a' },
  { key: 'primer',     name: 'Primer',       col: '#ffe082' },
  { key: 'detonator',  name: 'Detonator',    col: '#ff9e80' },
  { key: 'catalyst',   name: 'Catalyst',     col: '#ffcc80' },
  { key: 'static',     name: 'Static Cell',  col: '#fff176' },
  { key: 'bigshot',    name: 'Big Shells',   col: '#64b5f6' },
  { key: 'shellblast', name: 'Shell Blasts', col: '#ffab91' },
  { key: 'headhunter', name: 'Headhunter',   col: '#ef9a9a' },
  { key: 'blast',      name: 'Blast (other)',col: '#ffd24a' },   // fallback — should stay near zero now
  { key: 'chain',      name: 'Chain React.', col: '#ff9100' },
  { key: 'singularity',name: 'Singularity',  col: '#a06bff' },
  { key: 'overload',   name: 'Overload',     col: '#ff5db1' },
  { key: 'detonate',   name: 'DoT Detonate', col: '#ff7043' },
  { key: 'execute',    name: 'Reaper',       col: '#b39ddb' },
  { key: 'execmark',   name: 'Exec. Protocol', col: '#ffe9a8' },
  { key: 'drone',      name: 'Drone',        col: '#5ad1c0' },
  { key: 'aegis',      name: 'Aegis',        col: '#b0bec5' },
  { key: 'thorns',     name: 'Spikes',       col: '#9ccc65' },
  { key: 'reflect',    name: 'Reflector',    col: '#90caf9' },
  { key: 'flare',      name: 'Flashpoint',   col: '#ffa040' },   // 0.16 engine pass
  { key: 'icelance',   name: 'Ice Lance',    col: '#a5e6ff' },
  { key: 'overkill',   name: 'Overkill',     col: '#ffd6a0' },
  { key: 'scorch',     name: 'Scorch',       col: '#ff7a30' },   // 0.16 variant keystones
  { key: 'miasma',     name: 'Miasma',       col: '#9be06a' },
  { key: 'hail',       name: 'Hailstorm',    col: '#bfe2ff' },
  { key: 'bastion',    name: 'Bastion',      col: '#d0c8b0' },
  { key: 'phoenix',    name: 'Phoenix',      col: '#ffb45a' },   // 0.16 mythics
  { key: 'horizon',    name: 'Event Horizon',col: '#8affec' },
  { key: 'sunspot',    name: 'Sunspot',      col: '#ffd54f' },   // 0.18 expansion
  { key: 'avalanche',  name: 'Avalanche',    col: '#dceeff' },
  { key: 'leyden',     name: 'Leyden Jar',   col: '#aebfff' },
  { key: 'dynamo',     name: 'Dynamo',       col: '#9adcff' },
  { key: 'mine',       name: 'Minefield',    col: '#ffb74d' },
  { key: 'frag',       name: 'Frag Rounds',  col: '#8fb8d8' },
  { key: 'prism',      name: 'Prism Heart',  col: '#b6ffe9' },
  { key: 'breach',     name: 'Breach Charge',col: '#ffae5e' },   // 0.19 pool-gap pass
  { key: 'combustion', name: 'Combustion',   col: '#ff5a2a' },   // 0.22 new-path sources (one new sub-playstyle per archetype)
  { key: 'rupture',    name: 'Rupture',      col: '#a6d957' },
  { key: 'frostbite',  name: 'Frostbite',    col: '#aee3ff' },
  { key: 'stormfield', name: 'Storm Field',  col: '#b9a6ff' },
  { key: 'saturation', name: 'Saturation',   col: '#ffc266' },
  { key: 'coinstorm',  name: 'Coinstorm',    col: '#ffd95e' },
  { key: 'ram',        name: 'Momentum',     col: '#cdd6e0' },
  { key: 'insight',    name: 'Insight',      col: '#9ad0ff' },
  // 0.27 fun procs
  { key: 'frostbloom', name: 'Frostbloom',   col: '#a9e6ff' },
  { key: 'quake',      name: 'Quake Shot',   col: '#d8c19a' },
  { key: 'seeker',     name: 'Seeker Swarm', col: '#ffe07a' },
  { key: 'specter',    name: 'Necrotic',     col: '#b48cff' },
  { key: 'gravewell',  name: 'Gravewell',    col: '#9a7bd6' },
  { key: 'goldburst',  name: 'Coin Bomb',    col: '#ffd54a' },
  { key: 'pyreburst',  name: 'Pyre Burst',   col: '#ff7a3d' },
  { key: 'shatternova', name: 'Shatter Nova', col: '#bfe8ff' },
  { key: 'voidrift',   name: 'Void Rend',    col: '#8a6bff' },
  { key: 'critburst',  name: 'Crit Burst',   col: '#ff9aa6' },
  { key: 'decapitate', name: 'Decapitate',   col: '#ff6b6b' },
  { key: 'meteor',     name: 'Meteor',       col: '#ff9a5a' },
  { key: 'sentry',     name: 'Sentry Drone', col: '#9adcff' },
  { key: 'novapulse',  name: 'Nova Pulse',   col: '#a6b8ff' },
  { key: 'blizzardstrike', name: 'Blizzard', col: '#bfe8ff' },
  { key: 'dashnova',   name: 'Phase Nova',   col: '#c0a6ff' },
  { key: 'panicnova',  name: 'Panic Nova',   col: '#ffb0b0' },
  { key: 'vengeance',  name: 'Vengeance',    col: '#ff8a6b' },
  { key: 'railbeam',   name: 'Rail Sweep',   col: '#9fe0ff' },
  // 0.28 talent hero-layer (Tier-5 fork bespoke mechanics)
  { key: 'necrosis',     name: 'Necrosis',     col: '#9be06a' },
  { key: 'shatter',      name: 'Shatterstorm', col: '#bfe8ff' },
  { key: 'balllightning',name: 'Ball Lightning',col: '#9fb6ff' },
  { key: 'artillery',    name: 'Artillery',    col: '#ffbe6a' },
  { key: 'chainblast',   name: 'Chain Reaction',col: '#ff9a5a' },
  { key: 'fortress',     name: 'Fortress',     col: '#9fd0a0' },
];
const _warnedSrc = new Set();   // warn once per damage source that's missing a DMG_SRC_META row (renders as raw key)
export function srcMeta(k) {   // resolve any source key to {name, col} — 'w:blaster' → the weapon's name/icon
  const m = DMG_SRC_META.find(s => s.key === k);
  if (m) return m;
  if (k && k.slice(0, 2) === 'w:') {
    const w = WEAPONS[WEAPON_IDX[k.slice(2)]];
    if (w) return { key: k, name: w.icon + ' ' + w.name, col: '#7fd3ff' };
  }
  if (k && !_warnedSrc.has(k)) { _warnedSrc.add(k); console.warn('⚠ METER: damage source "' + k + '" has no DMG_SRC_META row — add one in meter.js (the parallel structure drifted)'); }
  return { key: k, name: k, col: '#9fb0e0' };
}
S.companion = null;   // active run ally entity (built from save.companion in reset), null for 'none'
S.lbTab = 'global';                          // 'global' (server) | 'local' (this device)
S.globalScores = null, S.globalState = 'idle'; // idle | loading | ok | error
S.lbVersion = GAME_VERSION, S.lbVersions = [GAME_VERSION];   // version selector: each balance patch is its own board, defaults to the running build
export const API = '/api/scores';                     // leaderboard (top 10), served by server.js (same origin)
export const API_RUN = '/api/run';                    // full per-run telemetry → appended to runs.jsonl
export const cam = { x: 0, y: 0 };
S.zoom = 1;          // camera zooms OUT at high speed so fast play stays readable
S.particles = [];
S.coins = [];        // collectible money scattered through the world
S.pickups = [];      // hearts (heal) & power shards (+run damage) dropped by enemies
S.shrines = [];      // item shrines you buy from mid-run
S.uiButtons = [];    // clickable shop buttons, rebuilt each menu render
S.shake = 0;
S.frame = 0;
S.effects = [];                 // spark/shock/boost visuals spawned by procs
S.chainCount = 0, S.chainTimer = 0;  // combo readout for cascades
S.procThisFrame = 0;            // per-frame proc budget guard
export const MAX_PROC_DEPTH = 6;         // how deep a proc cascade may recurse
